import { useMemo, useRef } from 'react';
import { useSuspenseQueries, type UseSuspenseQueryOptions } from '@tanstack/react-query';

/**
 * Resolve a list of entity ids to their cached entities via N parallel
 * suspense queries — one per id. Each id gets its own queryOptions
 * (typically `getXQueryOptions(workspaceId, id, qc)` from the entity's
 * queries.ts), so a cache miss for any id triggers that id's by-id
 * queryFn instead of a global refetch.
 *
 * **The "follow the FK" pattern** in TanStack Query terms. Lets us model
 * relations without storing a separate per-parent relation cache slot:
 *
 *   const actions = useEntitiesByIds<Action>(
 *     (id) => getActionQueryOptions(workspaceId, id, qc),
 *     campaign.workflow_action_ids,
 *   );
 *
 * **No duplication.** Each entity exists in exactly one detail cache slot.
 * Updating an action (via setQueryData on its detail slot from a mutation)
 * automatically refreshes everywhere it's referenced — every component
 * using this hook resubscribes through TanStack Query's per-query
 * notification.
 *
 * **Cache hits are O(1) per id** (no extra fetch, just denormalize-from-
 * cache). Cache misses fire the id's queryFn — useful for the "deep
 * link" case where some referenced entities aren't in cache yet.
 *
 * **Suspends** if any id's slot is empty AND its queryFn is in flight,
 * matching `useSuspenseQuery` semantics. Wrap call sites in a Suspense
 * boundary that's appropriate for "the relation collection is loading."
 *
 * **Misses fan out in parallel.** `useSuspenseQueries` fires every cache
 * miss concurrently — there's no waterfall on a deep link with N empty
 * slots. The component suspends once until the slowest miss settles.
 *
 * **Stabilize `optionsForId`** — wrap with `useCallback` keyed on the
 * captured deps (workspaceId, qc). An unstable function reference
 * defeats the queries-array memo, recreating every per-id option object
 * each render. In dev a `console.warn` fires the first time the
 * callback identity changes between renders.
 */
export function useEntitiesByIds<TEntity, TId = string>(
  optionsForId: (id: TId) => UseSuspenseQueryOptions<TEntity>,
  ids: readonly TId[],
): readonly TEntity[] {
  useUnstableOptionsForIdWarning(optionsForId);
  // The `ids` array often has the same contents across renders but a
  // fresh reference each time — `campaign.tag_ids` is one example, and
  // any caller that builds `ids` via `.map(...)` inline produces the
  // same shape. Memoize the queries array on a structural snapshot of
  // `ids` so we only re-run `optionsForId` when the contents actually
  // change. `JSON.stringify` produces an unambiguous, content-keyed
  // string for primitive id arrays (the `EntityId = string | number`
  // contract guarantees). O(N) per render, but tiny in practice
  // (FK arrays are tens of ids, not thousands).
  const idsKey = JSON.stringify(ids);
  const queries = useMemo(
    () => ids.map((id) => optionsForId(id)),
    // oxlint-disable-next-line react/exhaustive-deps -- ids/optionsForId captured via idsKey
    [idsKey, optionsForId],
  );
  const results = useSuspenseQueries({ queries });
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return results.map((q) => q.data as TEntity);
}

/**
 * Dev-only signal that the caller didn't memoize `optionsForId`. Fires
 * once per offending hook instance. Named with the `use` prefix so it
 * passes `react-hooks/rules-of-hooks`; called unconditionally from
 * `useEntitiesByIds` so the hook-call ordering stays consistent.
 */
function useUnstableOptionsForIdWarning(optionsForId: (id: never) => unknown): void {
  const previousRef = useRef<typeof optionsForId | null>(null);
  const warnedRef = useRef(false);
  if (import.meta.env.DEV && !warnedRef.current) {
    if (previousRef.current != null && previousRef.current !== optionsForId) {
      warnedRef.current = true;
      // oxlint-disable-next-line no-console -- dev-only signal that the memo contract is violated
      console.warn(
        '[useEntitiesByIds] `optionsForId` identity changed between renders. ' +
          'Wrap with `useCallback` so the queries-array memo can hit. ' +
          'See the JSDoc on `useEntitiesByIds`.',
      );
    }
  }
  previousRef.current = optionsForId;
}
